import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@heroui/react";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { api } from "../api/client";
import type { IngredientDetail } from "../api/client";
import { SHOP_ZONE_LABELS } from "../constants";

const PAGE_SIZE = 50;
const collator = new Intl.Collator("ca", { sensitivity: "base" });

const NOVA_COLOR: Record<number, string> = {
  1: "oklch(62% 0.13 150)",
  2: "oklch(72% 0.14 110)",
  3: "oklch(73% 0.15 62)",
  4: "oklch(60% 0.18 28)",
};
const NOVA_SHORT: Record<number, string> = {
  1: "No processat",
  2: "Ingredient culinari",
  3: "Processat",
  4: "Ultraprocessat",
};

const MACROS = [
  { key: "calories", label: "kcal", color: "oklch(68% 0.17 47)", max: 600, unit: "" },
  { key: "protein", label: "prot", color: "oklch(58% 0.13 300)", max: 30, unit: "g" },
  { key: "carbs", label: "carb", color: "oklch(70% 0.13 80)", max: 80, unit: "g" },
  { key: "fat", label: "greix", color: "oklch(66% 0.14 55)", max: 40, unit: "g" },
] as const;

interface IngredientPage {
  items: IngredientDetail[];
  total: number;
  hasMore: boolean;
}

interface BarcodeImportResult {
  id: number;
  ingredient?: IngredientDetail;
  created: boolean;
  matchedBy: "barcode" | "name" | null;
}

// Formats de codi de barres de producte habituals (EAN/UPC + Code 128).
const BARCODE_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
];

function barcodeHints() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, BARCODE_FORMATS);
  return hints;
}

function formatMacro(value: number, unit: string) {
  const rounded = Math.abs(value) >= 10 || value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
  return unit ? `${rounded}${unit}` : rounded;
}

function macroPct(value: number, max: number) {
  return `${Math.max(4, Math.min(100, (value / max) * 100))}%`;
}

function hasNutrition(ing: IngredientDetail) {
  return MACROS.some((m) => ing[m.key] != null);
}

function normalizeBarcode(value: string) {
  return value.replace(/\D/g, "");
}

async function detectBarcodeFromPhoto(file: File) {
  const reader = new BrowserMultiFormatReader(barcodeHints());
  const url = URL.createObjectURL(file);
  try {
    const result = await reader.decodeFromImageUrl(url);
    return normalizeBarcode(result.getText()) || null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

interface IngredientsAdminProps {
  aiOpen?: boolean;
}

export function IngredientsAdmin({ aiOpen = false }: IngredientsAdminProps) {
  const nav = useNavigate();
  const [ingredients, setIngredients] = useState<IngredientDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [q, setQ] = useState("");
  const [shopZones, setShopZones] = useState<string[]>([]);
  const [selectedShopZone, setSelectedShopZone] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const readerControlsRef = useRef<IScannerControls | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [barcodeBusy, setBarcodeBusy] = useState(false);
  const [barcodeStatus, setBarcodeStatus] = useState<string | null>(null);
  const [showManualBarcode, setShowManualBarcode] = useState(false);
  const [manualBarcode, setManualBarcode] = useState("");
  const safeIngredients = Array.isArray(ingredients) ? ingredients : [];

  function stopBarcodeScanner() {
    readerControlsRef.current?.stop();
    readerControlsRef.current = null;
    setScannerReady(false);
    setScannerOpen(false);
  }

  async function importBarcode(rawBarcode: string) {
    const barcode = normalizeBarcode(rawBarcode);
    if (!/^\d{6,18}$/.test(barcode)) {
      setBarcodeStatus("El codi de barres no sembla valid.");
      return;
    }

    setBarcodeBusy(true);
    setBarcodeStatus("Cercant a OpenFoodFacts...");
    try {
      const result = await api.post<BarcodeImportResult>("/ingredients/import-barcode", { barcode });
      window.dispatchEvent(new CustomEvent("cookmenu:data-changed"));
      const name = result.ingredient?.name ?? "ingredient";
      const status = result.created
        ? "Ingredient creat: " + name
        : result.matchedBy === "name"
          ? "Ingredient existent completat: " + name
          : "Ingredient ja existent: " + name;
      setBarcodeStatus(status);
      setShowManualBarcode(false);
      setManualBarcode("");
      stopBarcodeScanner();
      nav(`/ingredients/${result.id}`);
    } catch (e) {
      setBarcodeStatus(e instanceof Error ? e.message : "No s'ha pogut importar el producte.");
    } finally {
      setBarcodeBusy(false);
    }
  }

  // Espera que el <video> es munti al DOM (es renderitza en obrir el modal).
  async function waitForVideo(): Promise<HTMLVideoElement | null> {
    for (let i = 0; i < 20 && !videoRef.current; i++) {
      await new Promise((r) => setTimeout(r, 25));
    }
    return videoRef.current;
  }

  async function startBarcodeScanner() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setBarcodeStatus("Aquest navegador no permet obrir la càmera. Pots fer una foto o escriure el codi manualment.");
      setShowManualBarcode(true);
      return;
    }

    stopBarcodeScanner();
    setScannerOpen(true);
    setScannerReady(false);
    setBarcodeStatus("Obrint la càmera...");

    try {
      const video = await waitForVideo();
      if (!video) throw new Error("Video no disponible");

      const reader = new BrowserMultiFormatReader(barcodeHints());
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: "environment" } }, audio: false },
        video,
        (result) => {
          if (!result) return;
          const barcode = normalizeBarcode(result.getText());
          if (barcode) {
            stopBarcodeScanner();
            void importBarcode(barcode);
          }
        }
      );
      readerControlsRef.current = controls;
      setScannerReady(true);
      setBarcodeStatus("Enfoca el codi de barres dins del marc.");
    } catch {
      stopBarcodeScanner();
      setBarcodeStatus("No he pogut obrir la càmera. Pots fer una foto o escriure el codi manualment.");
      setShowManualBarcode(true);
    }
  }

  async function handleBarcodePhoto(file: File | undefined) {
    if (!file) return;
    setBarcodeBusy(true);
    setBarcodeStatus("Llegint el codi de barres...");
    try {
      const barcode = await detectBarcodeFromPhoto(file);
      if (barcode) {
        await importBarcode(barcode);
        return;
      }
      setBarcodeStatus("No he pogut llegir el codi de la foto. Pots escriure'l manualment.");
      setShowManualBarcode(true);
    } catch {
      setBarcodeStatus("No he pogut llegir el codi de la foto. Pots escriure'l manualment.");
      setShowManualBarcode(true);
    } finally {
      setBarcodeBusy(false);
    }
  }
  async function load(reset = false, queryValue = q, zoneValue = selectedShopZone) {
    const requestId = ++requestIdRef.current;
    const offset = reset ? 0 : ingredients.length;
    if (reset) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams({
        includeTotal: "1",
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      const query = queryValue.trim();
      if (query) params.set("q", query);
      if (zoneValue) params.set("shopZone", zoneValue);

      const data = await api.get<IngredientPage | IngredientDetail[]>(`/ingredients?${params.toString()}`);
      if (requestId !== requestIdRef.current) return;

      const page = Array.isArray(data)
        ? { items: data, total: reset ? data.length : ingredients.length + data.length, hasMore: false }
        : data;
      const items = Array.isArray(page.items) ? page.items : [];
      const nextTotal = Number.isFinite(page.total) ? page.total : reset ? items.length : ingredients.length + items.length;

      setIngredients((current) => (reset ? items : [...current, ...items]));
      setTotal(nextTotal);
      setHasMore(Boolean(page.hasMore));
    } catch (e) {
      console.error("Error loading ingredients:", e);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }

  async function loadShopZones() {
    try {
      const data = await api.get<string[]>("/ingredients/shop-zones");
      setShopZones(Array.isArray(data) ? data : []);
    } catch {
      setShopZones([]);
    }
  }

  useEffect(() => {
    load(true, "", null);
    loadShopZones();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(true, q, selectedShopZone), 250);
    return () => clearTimeout(timer);
  }, [q, selectedShopZone]);

  useEffect(() => {
    function onChanged() {
      load(true, q, selectedShopZone);
      loadShopZones();
    }
    window.addEventListener("cookmenu:data-changed", onChanged);
    return () => window.removeEventListener("cookmenu:data-changed", onChanged);
  }, [q, selectedShopZone]);

  useEffect(() => {
    return () => stopBarcodeScanner();
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) load(false, q, selectedShopZone);
      },
      { rootMargin: "360px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, safeIngredients.length, q, selectedShopZone]);

  const sorted = [...safeIngredients].sort((a, b) => collator.compare(a.name, b.name));
  const grouped = sorted.reduce<Record<string, IngredientDetail[]>>((acc, ing) => {
    const letter = (ing.name.trim()[0] || "#").toLocaleUpperCase("ca");
    (acc[letter] ||= []).push(ing);
    return acc;
  }, {});

  const groupLetters = Object.keys(grouped).sort((a, b) => collator.compare(a, b));
  const countLabel = total || safeIngredients.length;

  return (
    <div className="ingredients-page">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          e.currentTarget.value = "";
          void handleBarcodePhoto(file);
        }}
      />
      {/* Capçalera */}
      <div className="page-head mb-0">
        <div>
          <h1>Ingredients</h1>
          <div className="sub">
            {countLabel} {countLabel === 1 ? "ingredient" : "ingredients"} · valors per 100 g
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => nav("/ingredients/nova")}>+ Nou ingredient</button>
      </div>

      {/* Cerca */}
      <div className="ing-search">
        <span className="si" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.5" y2="16.5" />
          </svg>
        </span>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca un ingredient…"
        />
      </div>

      {/* Filtre per categoria */}
      {shopZones.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {shopZones.map((zone) => (
            <button
              key={zone}
              className={`chip ${selectedShopZone === zone ? "active" : ""}`}
              onClick={() => setSelectedShopZone(selectedShopZone === zone ? null : zone)}
            >
              {SHOP_ZONE_LABELS[zone] ?? zone}
            </button>
          ))}
        </div>
      )}

      {/* Llegenda NOVA */}
      <div className="nova-legend">
        <span className="nl-lbl">NOVA</span>
        {([1, 2, 3, 4] as const).map((n) => (
          <span key={n} className="nl-item">
            <i style={{ background: NOVA_COLOR[n] }} />
            {NOVA_SHORT[n]}
          </span>
        ))}
      </div>

      {/* Llista */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : safeIngredients.length === 0 ? (
        <div className="empty">
          <span style={{ fontSize: 32 }}>🧂</span>
          <span>Cap ingredient trobat</span>
        </div>
      ) : (
        <>
          <div className="ing-groups">
            {groupLetters.map((letter) => (
              <section key={letter}>
                <div className="ing-letter">{letter}</div>
                <div className="card ing-card">
                  {grouped[letter].map((ing, idx) => (
                    <button
                      key={ing.id}
                      type="button"
                      onClick={() => nav(`/ingredients/${ing.id}`)}
                      className="ing-row"
                      title={ing.novaGroup != null ? `NOVA ${ing.novaGroup} · ${NOVA_SHORT[ing.novaGroup]}` : undefined}
                      style={{ borderTop: idx ? "1px solid var(--border)" : 0 }}
                    >
                      <span
                        className="ing-nova-ind"
                        style={{ background: ing.novaGroup != null ? NOVA_COLOR[ing.novaGroup] : "var(--border-2)" }}
                        aria-hidden
                      />

                      {ing.imageUrl && (
                        <img
                          className="ing-photo"
                          src={ing.imageUrl}
                          alt={ing.name}
                          loading="lazy"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                        />
                      )}

                      <div className="ing-main">
                        <div className="ing-title">
                          <span className="ing-name">{ing.name}</span>
                          {ing.nameEn && <span className="ing-en">{ing.nameEn}</span>}
                        </div>
                        {(ing.allergens || ing.isPantry || ing.shopZone) && (
                          <div className="ing-flags">
                            {ing.isPantry && <span className="tag" style={{ background: "var(--surface-secondary)", color: "var(--muted)", fontSize: 11 }}>rebost</span>}
                            {ing.shopZone && !ing.isPantry && <span className="tag" style={{ background: "var(--surface-secondary)", color: "var(--muted)", fontSize: 11 }}>{ing.shopZone}</span>}
                            {ing.allergens && <span className="tag allergen-tag">⚠ {ing.allergens}</span>}
                          </div>
                        )}
                      </div>

                      {hasNutrition(ing) && (
                        <div className="nutri-strip ing-nutri" aria-label="Valors nutricionals per 100 g">
                          {MACROS.map((m) => {
                            const value = ing[m.key];
                            return (
                              <div className="nutri" key={m.key}>
                                <b>{formatMacro(value ?? 0, m.unit)}</b>
                                <div className="bar">
                                  <i style={{ width: macroPct(value ?? 0, m.max), background: m.color }} />
                                </div>
                                <small>{m.label}</small>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <span className="ing-go" aria-hidden>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 6 15 12 9 18" />
                        </svg>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div ref={sentinelRef} className="ing-load-more" aria-hidden={!hasMore && !loadingMore}>
            {loadingMore && <Spinner size="sm" />}
          </div>
        </>
      )}

      {!aiOpen && (
        <button
          type="button"
          className="barcode-fab"
          onClick={() => void startBarcodeScanner()}
          disabled={barcodeBusy}
          aria-label="Afegeix ingredient amb codi de barres"
          title="Afegeix amb codi de barres"
        >
          {barcodeBusy ? (
            <Spinner size="sm" color="current" />
          ) : (
            <BarcodeIcon />
          )}
        </button>
      )}

      {scannerOpen && (
        <div className="barcode-scan-scrim" role="dialog" aria-label="Escaneja codi de barres">
          <div className="barcode-scan">
            <div className="barcode-video-wrap">
              <video ref={videoRef} className="barcode-video" playsInline muted />
              <div className="barcode-frame" aria-hidden />
            </div>
            <div className="barcode-scan-actions">
              <button type="button" className="btn" onClick={() => fileInputRef.current?.click()}>
                Foto
              </button>
              <button type="button" className="btn" onClick={() => setShowManualBarcode(true)}>
                Manual
              </button>
              <button type="button" className="btn btn-primary" onClick={stopBarcodeScanner}>
                Cancel·la
              </button>
            </div>
            {!scannerReady && <div className="barcode-scan-loading"><Spinner size="sm" /> Obrint camera</div>}
          </div>
        </div>
      )}

      {(barcodeStatus || showManualBarcode) && (
        <div className="barcode-toast" role="status">
          <button type="button" className="barcode-toast-close" onClick={() => { setBarcodeStatus(null); setShowManualBarcode(false); }} aria-label="Tanca">
            x
          </button>
          {barcodeStatus && <p>{barcodeStatus}</p>}
          {showManualBarcode && (
            <form
              className="barcode-manual"
              onSubmit={(e) => {
                e.preventDefault();
                void importBarcode(manualBarcode);
              }}
            >
              <input
                className="nm-input"
                inputMode="numeric"
                autoComplete="off"
                placeholder="EAN / UPC"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e.target.value)}
              />
              <button type="submit" className="btn btn-primary" disabled={barcodeBusy}>
                Cercar
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function BarcodeIcon() {
  return (
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7V5a2 2 0 0 1 2-2h2" />
      <path d="M16 3h2a2 2 0 0 1 2 2v2" />
      <path d="M20 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M8 21H6a2 2 0 0 1-2-2v-2" />
      <path d="M7 8v8" />
      <path d="M10 8v8" />
      <path d="M14 8v8" />
      <path d="M17 8v8" />
    </svg>
  );
}
